import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isExternalShareSession } from "@/lib/externalShareToken";

export interface AiSuggestionTarget {
  /** T.ex. "arbetsberedning", "skr", "amp" */
  documentType?: string;
  documentId?: string | null;
  projectId?: string | null;
  fieldKey?: string;
  fieldLabel?: string;
}

export interface AiSuggestionRow {
  id: string;
  document_type: string;
  document_id: string | null;
  field_key: string;
  field_label: string | null;
  original_text: string;
  suggested_text: string;
  accepted: boolean;
  source: string;
  created_at: string;
}

/**
 * Sparar historik över AI:ns språkförslag så att det går att följa
 * vad som föreslogs och vad användaren valde.
 */
export const useAiSuggestionLog = () => {
  const logSuggestion = useCallback(
    async (
      target: AiSuggestionTarget,
      original: string,
      suggested: string,
      opts?: { accepted?: boolean; source?: string },
    ): Promise<string | null> => {
      if (!target?.documentType || !target?.fieldKey) return null;
      // Externa länkmottagare har ingen session – historiken loggas då inte.
      if (isExternalShareSession()) return null;
      try {
        const { data, error } = await supabase
          .from("ai_text_suggestions")
          .insert({
            document_type: target.documentType,
            document_id: target.documentId ?? null,
            project_id: target.projectId ?? null,
            field_key: target.fieldKey,
            field_label: target.fieldLabel ?? null,
            original_text: original,
            suggested_text: suggested,
            accepted: opts?.accepted ?? false,
            source: opts?.source ?? "field",
          })
          .select("id")
          .maybeSingle();
        if (error) {
          console.warn("Kunde inte spara AI-historik:", error.message);
          return null;
        }
        return data?.id ?? null;
      } catch (e) {
        console.warn("Kunde inte spara AI-historik", e);
        return null;
      }
    },
    [],
  );

  const markAccepted = useCallback(async (id: string | null) => {
    if (!id) return;
    try {
      await supabase.from("ai_text_suggestions").update({ accepted: true }).eq("id", id);
    } catch (e) {
      console.warn("Kunde inte uppdatera AI-historik", e);
    }
  }, []);

  const fetchHistory = useCallback(
    async (documentType: string, documentId?: string | null): Promise<AiSuggestionRow[]> => {
      let query = supabase
        .from("ai_text_suggestions")
        .select(
          "id, document_type, document_id, field_key, field_label, original_text, suggested_text, accepted, source, created_at",
        )
        .eq("document_type", documentType)
        .order("created_at", { ascending: false })
        .limit(200);

      if (documentId) query = query.eq("document_id", documentId);

      const { data, error } = await query;
      if (error) {
        console.warn("Kunde inte hämta AI-historik:", error.message);
        return [];
      }
      return (data ?? []) as AiSuggestionRow[];
    },
    [],
  );

  return { logSuggestion, markAccepted, fetchHistory };
};

export default useAiSuggestionLog;
